import { add, remove, qs } from "@fluorescent/dom";
import Delegate from "ftdomdelegate";

import { on, emit } from "@/glow/events";
import { makeRequest } from "@/lib/xhr";
import FreeShippingBar from "@/lib/free-shipping-bar";
import { animatePurchaseConfirmation, shouldAnimate } from "@/lib/animation";

const selectors = {
  container: "[js-purchase-confirmation-container]",
  closeButton: "[data-confirmation-close]",
  freeShippingBar: ".free-shipping-bar",
  header: "[js-header]",
  quickCart: ".quick-cart",
  viewCartButton: ".purchase-confirmation-popup__view-cart",
};

const classes = {
  active: "active",
  fixed: "fixed",
  hidden: "hidden",
};

const timings = {
  autoClose: 5000,
  mouseleaveGrace: 800, // delay before dismissing after cursor leaves
  minDisplay: 1200, // won't dismiss before this even if cursor leaves immediately
};

export default function PurchaseConfirmationPopup(node) {
  if (!node) return;
  const eventController = new AbortController();
  const eventOptions = { signal: eventController.signal };
  const quickCartEnabled = Boolean(qs(selectors.quickCart, document));
  const container = qs(selectors.container, node);
  const header = qs(selectors.header, document);
  const headerIsSticky = header.dataset.enableStickyHeader == "true";
  let purchaseConfirmationAnimation = null;

  if (shouldAnimate(node)) {
    purchaseConfirmationAnimation = animatePurchaseConfirmation(node);
  }

  let autoCloseTimeout = null;
  let mouseleaveTimeout = null;
  let openedAt = 0;

  const delegate = new Delegate(node);

  delegate.on("click", selectors.viewCartButton, event => {
    if (!quickCartEnabled) return;
    event.preventDefault();
    emit("quick-cart:open");
    close();
  });

  delegate.on("click", selectors.closeButton, event => {
    event.preventDefault();
    close();
  });

  on("confirmation-popup:open", (_, { product }) => getItem(product));

  function getItem(product) {
    const requestUrl = `${window.theme.routes.cart.base}/?section_id=purchase-confirmation-popup-item`;

    makeRequest("GET", requestUrl).then(response => {
      function inject() {
        let contentContainer = document.createElement("div");
        contentContainer.innerHTML = response;
        container.innerHTML = contentContainer.innerHTML;

        const freeShippingBar = qs(selectors.freeShippingBar, container);
        if (freeShippingBar) FreeShippingBar(freeShippingBar);

        // Show product within cart that was newly added
        const addedProduct = qs(`[data-product-key="${product.key}"]`, node);
        remove(addedProduct, classes.hidden);

        open();
      }

      if (node.classList.contains(classes.active) && shouldAnimate(node)) {
        purchaseConfirmationAnimation.exitProduct(inject);
      } else {
        inject();
      }
    });
  }

  function _getFullHeaderHeight() {
    const rootStyle = getComputedStyle(document.documentElement);
    const headerHeight = parseFloat(
      rootStyle.getPropertyValue("--height-header")
    );
    const announcementHeight = parseFloat(
      rootStyle.getPropertyValue("--announcement-height")
    );

    return headerHeight + announcementHeight;
  }

  function clearAllTimeouts() {
    clearTimeout(autoCloseTimeout);
    clearTimeout(mouseleaveTimeout);
  }

  function onMouseEnter() {
    clearAllTimeouts();
  }

  function onMouseLeave() {
    const elapsed = Date.now() - openedAt;
    const delay = Math.max(
      timings.mouseleaveGrace,
      timings.minDisplay - elapsed
    );
    mouseleaveTimeout = setTimeout(close, delay);
  }

  function initMouseEvents() {
    container.addEventListener("mouseenter", onMouseEnter, eventOptions);
    container.addEventListener("mouseleave", onMouseLeave, eventOptions);
  }

  function clearMouseEvents() {
    eventController.abort();
  }

  function open() {
    const alreadyOpen = node.classList.contains(classes.active);

    if (!headerIsSticky) {
      if (window.scrollY > _getFullHeaderHeight()) {
        container.classList.add(classes.fixed);
      } else {
        container.classList.remove(classes.fixed);
      }
    }

    add(node, classes.active);

    if (shouldAnimate(node)) {
      if (alreadyOpen) {
        purchaseConfirmationAnimation.animateSwap();
      } else {
        purchaseConfirmationAnimation.animate();
      }
    }

    openedAt = Date.now();
    clearAllTimeouts();
    autoCloseTimeout = setTimeout(close, timings.autoClose);

    initMouseEvents();
  }

  function close() {
    clearAllTimeouts();
    clearMouseEvents();

    if (shouldAnimate(node)) {
      purchaseConfirmationAnimation.reset();
      setTimeout(() => remove(node, classes.active), 150);
    } else {
      remove(node, classes.active);
    }
  }
}
